import React, { useState, useEffect } from 'react';
import {
    AppBar, Box, Toolbar, IconButton, Typography, Avatar, Button,
    Badge, Menu, MenuItem, Divider, ListItemIcon, Drawer, Tooltip,
    Fade, Zoom
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useDispatch, useSelector } from 'react-redux';
import { logOut } from '../../features/auth/authSlice';
import { getMenusUserIDWise } from '../../features/menu/menuSlice';
import { useNavigate } from 'react-router-dom';
import ModernSidebar from './ModernSidebar';
import '../../services/GlobalVariables';

const drawerWidth = 280;

export default function ModernNavbar() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector(state => state.auth);

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifAnchor, setNotifAnchor] = useState(null);

    useEffect(() => {
        if (!user) {
            navigate('/');
        } else {
            dispatch(getMenusUserIDWise({ userID: user.userID }));
        }
    }, [user, navigate, dispatch]);

    const handleProfileMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setAnchorEl(null);
    };

    const handleNotifOpen = (event) => {
        setNotifAnchor(event.currentTarget);
    };

    const handleNotifClose = () => {
        setNotifAnchor(null);
    };

    const handleLogout = () => {
        handleProfileMenuClose();
        dispatch(logOut(navigate));
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    return (
        <Box sx={{ display: 'flex' }}>
            {/* Modern AppBar */}
            <AppBar
                position="fixed"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    background: 'linear-gradient(135deg, #1E90FF 0%, #1c86ee 100%)',
                    boxShadow: '0 4px 20px rgba(30, 144, 255, 0.3)',
                    backdropFilter: 'blur(10px)',
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
                    {/* Left Section */}
                    <Box display="flex" alignItems="center" gap={2}>
                        <Tooltip title="Menu" arrow>
                            <IconButton
                                color="inherit"
                                onClick={toggleSidebar}
                                edge="start"
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.25)',
                                        transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Tooltip>

                        {/* Company Logo & Name */}
                        <Box display="flex" alignItems="center" gap={1.5}>
                            {user && (
                                <Zoom in timeout={500}>
                                    <Avatar
                                        src={`${global.SET_API_URL}/images/${user.cmpImage}`}
                                        alt={user.cmpName}
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            border: '3px solid rgba(255,255,255,0.3)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                        }}
                                    />
                                </Zoom>
                            )}
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 800,
                                        color: 'white',
                                        cursor: 'pointer',
                                        letterSpacing: '0.5px',
                                        '&:hover': { opacity: 0.9 },
                                    }}
                                    onClick={() => navigate('/Dashboard')}
                                >
                                    {user?.cmpName || 'HRMS'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'rgba(255,255,255,0.8)',
                                        fontWeight: 500,
                                    }}
                                >
                                    Human Resource Management
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Right Section */}
                    <Box display="flex" alignItems="center" gap={1}>
                        {/* Dashboard Button */}
                        <Tooltip title="Dashboard" arrow>
                            <IconButton
                                color="inherit"
                                onClick={() => navigate('/Dashboard')}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.25)',
                                    },
                                }}
                            >
                                <DashboardIcon />
                            </IconButton>
                        </Tooltip>

                        {/* Notifications */}
                        <Tooltip title="Notifications" arrow>
                            <IconButton
                                color="inherit"
                                onClick={handleNotifOpen}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.25)',
                                    },
                                }}
                            >
                                <Badge badgeContent={3} color="error">
                                    <NotificationsIcon />
                                </Badge>
                            </IconButton>
                        </Tooltip>

                        {/* User Profile */}
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.15)',
                                borderRadius: 3,
                                px: 2,
                                py: 0.5,
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.25)',
                                },
                                transition: 'all 0.3s ease',
                            }}
                            onClick={handleProfileMenuOpen}
                        >
                            <Box textAlign="right">
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'white',
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    {user?.empName || 'User'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'rgba(255,255,255,0.8)',
                                        fontSize: '0.7rem',
                                    }}
                                >
                                    {user?.userType || 'Administrator'}
                                </Typography>
                            </Box>
                            <Avatar
                                sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: 'white',
                                    color: '#1E90FF',
                                    fontWeight: 700,
                                    border: '2px solid rgba(255,255,255,0.5)',
                                }}
                            >
                                {user?.empName?.charAt(0) || 'U'}
                            </Avatar>
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Notifications Menu */}
            <Menu
                anchorEl={notifAnchor}
                open={Boolean(notifAnchor)}
                onClose={handleNotifClose}
                TransitionComponent={Fade}
                PaperProps={{
                    sx: {
                        mt: 1.5,
                        minWidth: 320,
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    }
                }}
            >
                <Box sx={{ p: 2, pb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>
                        Notifications
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        You have 3 unread messages
                    </Typography>
                </Box>
                <Divider />
                <MenuItem sx={{ py: 1.5 }}>
                    <Box>
                        <Typography variant="body2" fontWeight={600}>
                            New Employee Registered
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            John Doe has been added to the system
                        </Typography>
                    </Box>
                </MenuItem>
                <MenuItem sx={{ py: 1.5 }}>
                    <Box>
                        <Typography variant="body2" fontWeight={600}>
                            Attendance Marked
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Today's attendance has been recorded
                        </Typography>
                    </Box>
                </MenuItem>
                <MenuItem sx={{ py: 1.5 }}>
                    <Box>
                        <Typography variant="body2" fontWeight={600}>
                            Salary Sheet Ready
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Monthly salary sheet is available
                        </Typography>
                    </Box>
                </MenuItem>
            </Menu>

            {/* Profile Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleProfileMenuClose}
                TransitionComponent={Fade}
                PaperProps={{
                    sx: {
                        mt: 1.5,
                        minWidth: 220,
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    }
                }}
            >
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {user?.empName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {user?.empEmail || user?.empContact}
                    </Typography>
                </Box>
                <Divider />
                {/* <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/Profile'); }}>
                    <ListItemIcon>
                        <AccountCircleIcon fontSize="small" />
                    </ListItemIcon>
                    My Profile
                </MenuItem> */}
                <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/ChangePassword'); }}>
                    <ListItemIcon>
                        <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    Change Password
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ color: '#f44336' }}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" sx={{ color: '#f44336' }} />
                    </ListItemIcon>
                    Logout
                </MenuItem>
            </Menu>

            {/* Modern Sidebar Drawer */}
            <Drawer
                variant="temporary"
                open={sidebarOpen}
                onClose={toggleSidebar}
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
                        borderRight: '1px solid rgba(30, 144, 255, 0.1)',
                        boxShadow: '4px 0 20px rgba(0,0,0,0.08)',
                    },
                }}
            >
                {/* Drawer Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        background: 'linear-gradient(135deg, #1E90FF 0%, #1c86ee 100%)',
                        color: 'white',
                    }}
                >
                    <Typography variant="h6" fontWeight={800}>
                        Menu
                    </Typography>
                    <IconButton onClick={toggleSidebar} sx={{ color: 'white' }}>
                        <ChevronLeftIcon />
                    </IconButton>
                </Box>

                {/* Sidebar Content */}
                {user ? <ModernSidebar onClose={toggleSidebar} /> : null}
            </Drawer>

            {/* Toolbar Spacer */}
            <Toolbar />
        </Box>
    );
}
